import React, { useState, useCallback } from "react";
import JSZip from "jszip";
import {
    Upload,
    FileArchive,
    AlertTriangle,
    CheckCircle2,
    Loader2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@shadcn-in-nx/ui/alert";
import { Button } from "@shadcn-in-nx/ui/button";
import { useAuth } from "../../context/AuthContext";

function ImportProjectPanel({
    stepId,
    selectedEnvironment,
    onImportComplete,
    fetchComponents,
    reloadIframe,
}) {
    const { t } = useTranslation();
    const { isSuperAdmin } = useAuth();
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState({
        total: 0,
        processed: 0,
        successful: 0,
        failed: 0,
        errors: [],
    });
    const [selectedFile, setSelectedFile] = useState(null);

    const { call: createComponentApi } = useBluelagoon({
        method: "createComponent",
    });
    const { call: getComponentsByStepId } = useBluelagoon({
        method: "getComponentsByStepId",
    });
    const { call: updateComponentApi } = useBluelagoon({
        method: "updateComponent",
    });

    // Only show for super admins
    if (!isSuperAdmin) {
        return null;
    }

    const handleFileSelect = useCallback(
        (event) => {
            const file = event.target.files?.[0];
            if (file) {
                if (
                    file.type === "application/zip" ||
                    file.name.endsWith(".zip")
                ) {
                    setSelectedFile(file);
                } else {
                    toast.error(
                        t("appPreview.invalidFileType") ||
                            "Please select a ZIP file"
                    );
                    event.target.value = "";
                }
            }
        },
        [t]
    );

    const handleImport = useCallback(async () => {
        if (!selectedFile || !stepId) {
            toast.error(
                t("appPreview.noFileSelected") ||
                    "Please select a file to import"
            );
            return;
        }

        setIsImporting(true);
        setImportProgress({
            total: 0,
            processed: 0,
            successful: 0,
            failed: 0,
            errors: [],
        });

        try {
            // Read ZIP file
            const zip = await JSZip.loadAsync(selectedFile);
            const files = Object.keys(zip.files);

            // Filter out directories and hidden files
            const fileEntries = files.filter((fileName) => {
                const file = zip.files[fileName];
                return (
                    !file.dir &&
                    !fileName.startsWith("__MACOSX") &&
                    !fileName.startsWith(".")
                );
            });

            setImportProgress((prev) => ({
                ...prev,
                total: fileEntries.length,
            }));

            const existingComponentsResult = await getComponentsByStepId({
                stepId,
                environmentName: selectedEnvironment,
            });
            const componentsByFilePath = new Map();
            if (
                existingComponentsResult?.status === 200 &&
                Array.isArray(existingComponentsResult?.data?.components)
            ) {
                for (const c of existingComponentsResult.data.components) {
                    if (c.filePath) {
                        componentsByFilePath.set(c.filePath, c.id);
                    }
                }
            }

            let successful = 0;
            let failed = 0;
            const errors = [];

            // Process each file
            for (let i = 0; i < fileEntries.length; i++) {
                const fileName = fileEntries[i];
                const file = zip.files[fileName];

                try {
                    // Read file content
                    const fileContent = await file.async("string");

                    // Extract file path and name
                    const pathParts = fileName.split("/");
                    const actualFileName = pathParts[pathParts.length - 1];
                    const folderPath =
                        pathParts.length > 1
                            ? pathParts.slice(0, -1).join("/")
                            : null;

                    // Determine file extension
                    const hasExtension = actualFileName.includes(".");
                    const extension = hasExtension
                        ? actualFileName.substring(
                              actualFileName.lastIndexOf(".")
                          )
                        : ".jsx";

                    // Extract component name
                    const componentName = hasExtension
                        ? actualFileName.substring(
                              0,
                              actualFileName.lastIndexOf(".")
                          )
                        : actualFileName;

                    // Determine component type
                    let componentType = "COMPONENT";
                    if (
                        extension === ".jsx" &&
                        (fileName.includes("pages") ||
                            fileName.includes(".page"))
                    ) {
                        componentType = "PAGE";
                    } else if (fileName.includes("entities")) {
                        componentType = "ENTITY";
                    }

                    // Build file path
                    const filePath = folderPath
                        ? `${folderPath}/${actualFileName}`
                        : actualFileName;

                    const metadata = {
                        filePath,
                        isNonJsFile:
                            extension !== ".jsx" &&
                            extension !== ".js" &&
                            extension !== ".tsx" &&
                            extension !== ".ts",
                        imported: true,
                    };

                    const existingComponentId =
                        componentsByFilePath.get(filePath);
                    const result = existingComponentId
                        ? await updateComponentApi({
                              id: existingComponentId,
                              environmentName: selectedEnvironment,
                              name: actualFileName,
                              type: componentType,
                              code: fileContent,
                              description: `${componentName} component`,
                              filePath,
                              metadata,
                          })
                        : await createComponentApi({
                              stepId,
                              name: actualFileName,
                              environmentName: selectedEnvironment,
                              type: componentType,
                              code: fileContent,
                              description: `${componentName} component`,
                              filePath,
                              metadata,
                          });

                    const ok =
                        result?.status === 200 ||
                        result?.status === 201 ||
                        result?.data?.component;
                    if (ok) {
                        successful++;
                    } else {
                        failed++;
                        errors.push({
                            fileName,
                            error: result?.data?.message || "Unknown error",
                        });
                    }
                } catch (error) {
                    failed++;
                    errors.push({
                        fileName,
                        error: error.message || "Failed to process file",
                    });
                }

                setImportProgress((prev) => ({
                    ...prev,
                    processed: i + 1,
                    successful,
                    failed,
                    errors,
                }));
            }

            // Show results
            if (failed === 0) {
                toast.success(
                    t("appPreview.importSuccess", { count: successful }) ||
                        `Successfully imported ${successful} file(s)`
                );
            } else if (successful > 0) {
                toast.warning(
                    t("appPreview.importPartialSuccess", {
                        successful,
                        failed,
                    }) || `Imported ${successful} file(s), ${failed} failed`
                );
            } else {
                toast.error(
                    t("appPreview.importFailed") ||
                        "Failed to import project files"
                );
            }

            // Refresh components and reload iframe
            if (successful > 0) {
                if (fetchComponents) {
                    fetchComponents();
                }
                if (reloadIframe) {
                    reloadIframe();
                }
                if (onImportComplete) {
                    onImportComplete();
                }
            }

            // Reset file selection
            setSelectedFile(null);
            const fileInput = document.getElementById("import-project-file");
            if (fileInput) {
                fileInput.value = "";
            }
        } catch (error) {
            console.error("Import error:", error);
            toast.error(
                error?.message ||
                    t("appPreview.importError") ||
                    "Failed to import project"
            );
        } finally {
            setIsImporting(false);
        }
    }, [
        selectedFile,
        stepId,
        selectedEnvironment,
        createComponentApi,
        getComponentsByStepId,
        updateComponentApi,
        fetchComponents,
        reloadIframe,
        onImportComplete,
        t,
    ]);

    const handleClearFile = useCallback(() => {
        setSelectedFile(null);
        const fileInput = document.getElementById("import-project-file");
        if (fileInput) {
            fileInput.value = "";
        }
        setImportProgress({
            total: 0,
            processed: 0,
            successful: 0,
            failed: 0,
            errors: [],
        });
    }, []);

    return (
        <div className="w-full h-full overflow-auto bg-gray-50 p-4 md:p-6 pb-8">
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <FileArchive className="w-5 h-5 text-gray-600" />
                        <h2 className="text-lg font-semibold text-gray-900">
                            {t("appPreview.importProject") || "Import Project"}
                        </h2>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            {t("appPreview.superAdminOnly") ||
                                "Super Admin Only"}
                        </span>
                    </div>

                    <Alert className="mb-6 bg-yellow-50 border-yellow-200">
                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                        <AlertTitle className="text-yellow-800">
                            {t("appPreview.importWarning") || "Warning"}
                        </AlertTitle>
                        <AlertDescription className="text-yellow-700">
                            {t("appPreview.importWarningDescription") ||
                                "Importing a project will create new components from the ZIP file. Existing components with the same file path will be skipped or updated. This action cannot be undone."}
                        </AlertDescription>
                    </Alert>

                    <div className="space-y-4">
                        {/* File Selection */}
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                            <input
                                id="import-project-file"
                                type="file"
                                accept=".zip"
                                onChange={handleFileSelect}
                                disabled={isImporting}
                                className="hidden"
                            />
                            <label
                                htmlFor="import-project-file"
                                className="cursor-pointer flex flex-col items-center"
                            >
                                <Upload className="w-12 h-12 text-gray-400 mb-4" />
                                <p className="text-sm font-medium text-gray-700 mb-1">
                                    {selectedFile
                                        ? selectedFile.name
                                        : t("appPreview.selectZipFile") ||
                                          "Select a ZIP file to import"}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {t("appPreview.zipFileDescription") ||
                                        "Upload a ZIP file containing your project files"}
                                </p>
                            </label>
                        </div>

                        {/* Selected File Info */}
                        {selectedFile && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <FileArchive className="w-5 h-5 text-blue-600" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {selectedFile.name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {(
                                                    selectedFile.size / 1024
                                                ).toFixed(2)}{" "}
                                                KB
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClearFile}
                                        disabled={isImporting}
                                    >
                                        {t("common.clear") || "Clear"}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Progress */}
                        {isImporting && importProgress.total > 0 && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700">
                                        {t("appPreview.importing") ||
                                            "Importing..."}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                        {importProgress.processed} /{" "}
                                        {importProgress.total}
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                        style={{
                                            width: `${
                                                (importProgress.processed /
                                                    importProgress.total) *
                                                100
                                            }%`,
                                        }}
                                    />
                                </div>
                                <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
                                    <span className="flex items-center gap-1">
                                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                                        {importProgress.successful}{" "}
                                        {t("common.successful") || "successful"}
                                    </span>
                                    {importProgress.failed > 0 && (
                                        <span className="flex items-center gap-1">
                                            <AlertTriangle className="w-4 h-4 text-red-600" />
                                            {importProgress.failed}{" "}
                                            {t("common.failed") || "failed"}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Error List */}
                        {importProgress.errors.length > 0 && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <h3 className="text-sm font-medium text-red-800 mb-2">
                                    {t("appPreview.importErrors") ||
                                        "Import Errors"}
                                </h3>
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {importProgress.errors.map(
                                        (error, index) => (
                                            <div
                                                key={index}
                                                className="text-xs text-red-700"
                                            >
                                                <span className="font-medium">
                                                    {error.fileName}:
                                                </span>{" "}
                                                {error.error}
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Import Button */}
                        <div className="flex justify-end">
                            <Button
                                onClick={handleImport}
                                disabled={!selectedFile || isImporting}
                                className="min-w-[120px]"
                            >
                                {isImporting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {t("appPreview.importing") ||
                                            "Importing..."}
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        {t("appPreview.import") || "Import"}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ImportProjectPanel;
